import { LogicNodeDefinition } from '../LogicNodeDefinition';
import type { SimulationTiming } from '../SimulationEventQueue';

interface ClockState {
  intervalMs: number;
  timeAccumulator: number;
  isOn: boolean;
  enabled?: boolean;
}

const schedulers = new WeakMap<
  ClockState,
  {
    timing: SimulationTiming;
    last: number;
    initialized: boolean;
    schedule: () => void;
    deadline: number | undefined;
  }
>();

export const ClockNode: LogicNodeDefinition<ClockState> = {
  type: 'CLOCK',
  configurationKeys: ['intervalMs'],
  createDefaultState: () => ({
    intervalMs: 1000,
    timeAccumulator: 0,
    isOn: false,
  }),
  setup: (state, trigger, timing) => {
    // Standalone callers retain elapsed-time processing. Simulations provide a
    // chronological queue so each edge settles before the next deadline.
    // intervalMs is a minimum half-cycle: under load the oscillator stretches
    // its cadence, rather than manufacturing a burst of historical edges.
    // There is only one issued edge pending at a time (unlike transport Delay).
    if (!timing) {
      const id = setInterval(trigger, 100);
      return () => clearInterval(id);
    }
    let cancel: (() => void) | undefined;
    const restored = timing.restoredScheduler;
    if (restored && restored.kind !== 'clock') throw Error('Invalid Clock continuation');
    const issue = (next: number | undefined) => {
      cancel?.();
      cancel = undefined;
      runtime.deadline = next;
      if (next !== undefined)
        cancel = timing.schedule(next, () => {
          runtime.deadline = undefined;
          cancel = undefined;
          runtime.initialized = true;
          trigger();
        });
    };
    const runtime = {
      timing,
      last: restored?.last ?? timing.now(),
      initialized: restored?.initialized ?? false,
      deadline: undefined as number | undefined,
      schedule: () => {
        const next =
          state.enabled && Number.isFinite(state.intervalMs) && state.intervalMs > 0
            ? runtime.last + Math.max(0, state.intervalMs - state.timeAccumulator)
            : undefined;
        if (next === runtime.deadline) return;
        issue(next);
      },
    };
    schedulers.set(state, runtime);
    if (restored) issue(restored.deadline ?? undefined);
    else runtime.schedule();
    return () => {
      // Paused time is excluded; phase is the last processed simulation phase.
      cancel?.();
      schedulers.delete(state);
    };
  },
  needsInputSync: (input, state) =>
    input !== state.enabled || (!input && state.timeAccumulator !== 0),
  process: (input, state, deltaTime) => {
    const runtime = schedulers.get(state);
    if (runtime) {
      const now = runtime.timing.now();
      let nextPhaseStart = runtime.initialized ? Math.max(runtime.last, now) : now;
      if (input) {
        if (state.enabled && runtime.initialized)
          state.timeAccumulator += Math.max(0, now - runtime.last);
        if (state.intervalMs > 0 && state.timeAccumulator >= state.intervalMs) {
          state.timeAccumulator = 0;
          state.isOn = !state.isOn;
          // Preserve the issued edge, but do not enqueue periods the oscillator
          // could not deliver. A late user-input catch-up therefore consumes at
          // most this one clock edge, then returns control to the browser.
          nextPhaseStart = Math.max(now, runtime.timing.wallNow?.() ?? Date.now());
        }
      } else {
        state.isOn = false;
        state.timeAccumulator = 0;
        nextPhaseStart = now;
      }
      state.enabled = input;
      runtime.last = nextPhaseStart;
      runtime.initialized = true;
      runtime.schedule();
      return state.isOn;
    }
    if (input) {
      state.timeAccumulator += deltaTime;
      if (state.intervalMs > 0 && state.timeAccumulator >= state.intervalMs) {
        const toggles = Math.floor(state.timeAccumulator / state.intervalMs);
        state.timeAccumulator %= state.intervalMs;
        if (toggles % 2 === 1) state.isOn = !state.isOn;
      }
    } else {
      state.isOn = false;
      state.timeAccumulator = 0;
    }
    return state.isOn;
  },
};

/** Private scheduler phase, captured without processing or issuing an edge. */
export function captureClockScheduler(state: ClockState) {
  const runtime = schedulers.get(state);
  return runtime?.timing.resumable
    ? {
        kind: 'clock' as const,
        last: runtime.last,
        initialized: runtime.initialized,
        deadline: runtime.deadline ?? null,
      }
    : null;
}
